"use client";

import { Button } from "@workspace/ui/components/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@workspace/ui/components/drawer";
import { VisuallyHidden } from "@workspace/ui/components/visually-hidden";
import { type MouseEvent, useCallback, useState } from "react";
import { MarketplaceJobDetailsCard } from "./marketplace-job-details-card";
import { MarketplaceJobMarkerLabel } from "./marketplace-job-marker-label";
import type { MarketplaceJob } from "./use-cluster";

export function MarketplaceJobDrawer({
  job,
  isOpen,
  onActivate,
  onOpenChange,
  isActive,
  isHovered,
}: {
  job: MarketplaceJob;
  isOpen?: boolean;
  onActivate?: () => void;
  onOpenChange?: (open: boolean) => void;
  /** Phase 5: marker highlight when its row is selected/hovered. */
  isActive?: boolean;
  isHovered?: boolean;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const posterName = job.postedBy ?? "unknown";

  const controlled = onOpenChange !== undefined;
  const open = controlled ? (isOpen ?? false) : internalOpen;

  const isFavourited = job.isFavourited;

  const handleDrawerOpenChange = (newOpen: boolean) => {
    if (loginModalOpen && !newOpen) {
      return;
    }
    if (controlled) {
      if (!newOpen) {
        onOpenChange?.(false);
      }
      return;
    }
    setInternalOpen(newOpen);
  };

  const handleLabelActivate = useCallback(
    (event: MouseEvent<HTMLSpanElement>) => {
      event.stopPropagation();
      onActivate?.();
    },
    [onActivate]
  );

  return (
    <Drawer onOpenChange={handleDrawerOpenChange} open={open}>
      <MarketplaceJobMarkerLabel
        isActive={isActive}
        isFavourited={isFavourited}
        isHovered={isHovered}
        onClick={handleLabelActivate}
        onPointerDown={(event) => {
          event.stopPropagation();
        }}
        title={job.title}
      />
      <DrawerContent className="max-h-[calc(100dvh-4rem)]">
        <VisuallyHidden>
          <DrawerHeader>
            <DrawerTitle>{job.title}</DrawerTitle>
            <DrawerDescription>
              Job details for {job.title} at {posterName}
            </DrawerDescription>
          </DrawerHeader>
        </VisuallyHidden>
        <div className="min-h-0 flex-1 overflow-y-auto px-4">
          <MarketplaceJobDetailsCard
            cardClassName="border-none bg-transparent shadow-none"
            footerClassName="px-6 py-3"
            job={job}
            loginPreferDialog
            onLoginModalOpenChange={setLoginModalOpen}
            showContentBorder={false}
          />
        </div>
        <DrawerFooter>
          <DrawerClose asChild>
            <Button className="w-full" variant="outline">
              Close
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
